import type { CSSProperties } from 'react';
import { BusinessUnitSelector } from '../components/BusinessUnitSelector';
import { SOVEREIGN } from '../../../epc/utils/epcProjectVisual';
import type { FeatureFormApi } from '../useFeatureForm';
import type { MultiDeptValue } from '../../../../types/feature';

export function PlacementStep({ form }: { form: FeatureFormApi }) {
  const {
    route, setRoute, isGroupMenu, setIsGroupMenu, errors,
    department, setDepartment, useMultipleDepts, setUseMultipleDepts,
    selectedDepartments, setSelectedDepartments, moduleInput,
    parentKey, setParentKey, parentFeatures, order, setOrder, isActive, setIsActive,
  } = form;

  return (
    <div className="space-y-5">
      <p
        className="text-sm"
        style={{ color: SOVEREIGN.kpiCaption }}
      >
        Decide where the feature lives: which business units see it, its route, and its place in the menu.
      </p>

      <div>
        <label
          className="block text-xs font-semibold mb-2"
          style={{ color: SOVEREIGN.ink }}
        >
          Business Unit <span className="text-red-500">*</span>
        </label>
        <BusinessUnitSelector
          department={department}
          useMultipleDepts={useMultipleDepts}
          selectedDepartments={selectedDepartments}
          moduleInput={moduleInput}
          error={errors.businessUnit}
          onSelectSingle={(d) => { setDepartment(d); setUseMultipleDepts(false); }}
          onUseMultiple={() => setUseMultipleDepts(true)}
          onToggleDept={(d: MultiDeptValue) =>
            setSelectedDepartments(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d])}
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <label
            className="text-xs font-semibold"
            style={{ color: SOVEREIGN.ink }}
          >
            Route Path {!isGroupMenu && <span className="text-red-500">*</span>}
          </label>
          <label
            className="flex items-center gap-1.5 text-xs cursor-pointer"
            style={{ color: SOVEREIGN.kpiCaption }}
          >
            <input
              type="checkbox"
              checked={isGroupMenu}
              onChange={e => setIsGroupMenu(e.target.checked)}
              className="h-3.5 w-3.5 rounded"
              style={{ accentColor: SOVEREIGN.bottle } as CSSProperties}
            />
            Group Menu only (no route)
          </label>
        </div>
        <input
          type="text"
          value={isGroupMenu ? '#' : route}
          onChange={e => setRoute(e.target.value)}
          disabled={isGroupMenu}
          placeholder="e.g., /projects, /masters/assets"
          className="w-full px-3 py-2 text-sm font-mono rounded-lg focus:ring-2 focus:outline-none transition-colors"
          style={
            isGroupMenu
              ? { border: `1px solid ${SOVEREIGN.innerRule}`, background: SOVEREIGN.paper, color: SOVEREIGN.kpiCaption }
              : errors.route
                ? { border: '1px solid #fca5a5', background: '#fff', color: SOVEREIGN.ink }
                : { border: `1px solid ${SOVEREIGN.inputBorder}`, background: SOVEREIGN.inputBg, color: SOVEREIGN.ink }
          }
        />
        {errors.route && <p className="text-xs text-red-600 mt-1">{errors.route}</p>}
        {isGroupMenu && (
          <p
            className="text-xs mt-1"
            style={{ color: SOVEREIGN.kpiCaption }}
          >
            Group menus use "#" — they expand to show sub-items
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label
            className="block text-xs font-semibold mb-1"
            style={{ color: SOVEREIGN.ink }}
          >
            Parent Feature{' '}
            <span
              className="font-normal"
              style={{ color: SOVEREIGN.kpiCaption }}
            >
              (optional)
            </span>
          </label>
          <select
            value={parentKey}
            onChange={e => setParentKey(e.target.value)}
            className="w-full px-3 py-2 text-sm rounded-lg focus:ring-2 focus:outline-none"
            style={{
              border: `1px solid ${SOVEREIGN.inputBorder}`,
              background: SOVEREIGN.inputBg,
              color: SOVEREIGN.ink,
            }}
          >
            <option value="">— Top Level (no parent) —</option>
            {parentFeatures.map(f => (
              <option key={f.key} value={f.key}>{f.name} ({f.key})</option>
            ))}
          </select>
          <p
            className="text-xs mt-1"
            style={{ color: SOVEREIGN.kpiCaption }}
          >
            Sub-items nest inside their parent
          </p>
        </div>
        <div>
          <label
            className="block text-xs font-semibold mb-1"
            style={{ color: SOVEREIGN.ink }}
          >
            Display Order
          </label>
          <input
            type="number"
            value={order}
            min={0}
            onChange={e => setOrder(parseInt(e.target.value) || 0)}
            className="w-full px-3 py-2 text-sm rounded-lg focus:ring-2 focus:outline-none"
            style={{
              border: `1px solid ${SOVEREIGN.inputBorder}`,
              background: SOVEREIGN.inputBg,
              color: SOVEREIGN.ink,
            }}
          />
          <p
            className="text-xs mt-1"
            style={{ color: SOVEREIGN.kpiCaption }}
          >
            Lower = appears first
          </p>
        </div>
      </div>

      <div
        className="flex items-center gap-3 p-3 rounded-xl"
        style={{
          background: SOVEREIGN.inputBg,
          border: `1px solid ${SOVEREIGN.cardBorder}`,
        }}
      >
        <button
          type="button"
          onClick={() => setIsActive(!isActive)}
          className="relative w-10 h-5 rounded-full transition-colors"
          style={{ background: isActive ? SOVEREIGN.brand : SOVEREIGN.barTrack }}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${isActive ? 'translate-x-5' : ''}`}
          />
        </button>
        <div>
          <p
            className="text-sm font-semibold"
            style={{ color: SOVEREIGN.ink }}
          >
            {isActive ? 'Active' : 'Inactive'}
          </p>
          <p
            className="text-xs"
            style={{ color: SOVEREIGN.kpiCaption }}
          >
            {isActive ? 'Feature will appear in menus for users with this permission' : 'Feature is hidden from all menus'}
          </p>
        </div>
      </div>
    </div>
  );
}
